import { PasswordStrength } from "../types";

export const PASSWORD_SPECIAL_CHARACTERS = "~!@#$%^&*()_+-.=?<>,:;{}|" as const;

export const passwordChars = {
    numbers: "0123456789",
    lowercaseChars: "abcdefghijklmnopqrstuvwxyz",
    uppercaseChars: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
    specialChars: PASSWORD_SPECIAL_CHARACTERS,
};

function snakeToCamel(s: string): string {
    return s.replace(/(_\w)/g, (m) => m[1].toUpperCase());
}

export function randomString(len: number, choices: string) {
    let chars = "";
    for (let i = 0; i < len; i++) chars += choices.charAt(Math.floor(Math.random() * choices.length));
    return chars;
}

export function shuffleString(str: string): string {
    const a = str.split("");
    const n = a.length;

    for (let i = n - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = a[i];
        a[i] = a[j];
        a[j] = tmp;
    }
    return a.join("");
}

export function generator() {
    let chars = "";
    const minLength = window.passwordStrength["minimum_length"] || 0;
    for (const configName in window.passwordStrength) {
        if (!configName.startsWith("required_")) continue;
        const limit = window.passwordStrength[configName];
        if (limit < 1) continue;
        const choices = passwordChars[snakeToCamel(configName.replace("required_", ""))];
        chars += randomString(limit, choices);
    }
    if (chars.length < minLength)
        chars += randomString(minLength - chars.length, passwordChars.lowercaseChars + passwordChars.numbers);

    if (chars.length == 0 && minLength == 0)
        return randomString(12, passwordChars.lowercaseChars + passwordChars.numbers + passwordChars.uppercaseChars);

    return shuffleString(chars);
}

/// a message that can be used with formatMessage()
type MessageDescriptor = {
    id: string;
    values: any;
};

/// Returns a list of errors for the given password and strength configuration.
export function findErrors(strengthConfig: PasswordStrength, password: string): MessageDescriptor[] {
    const errors: MessageDescriptor[] = [];

    if (strengthConfig.minimum_length && password.length < strengthConfig.minimum_length)
        errors.push({ id: "validation.password.length", values: { min: strengthConfig.minimum_length } });

    if (strengthConfig.required_lowercase_chars) {
        const count = (password.match(/[a-z]/g) || []).length;
        if (count < strengthConfig.required_lowercase_chars)
            errors.push({
                id: "validation.password.lowercase",
                values: { min: strengthConfig.required_lowercase_chars },
            });
    }

    if (strengthConfig.required_uppercase_chars) {
        const count = (password.match(/[A-Z]/g) || []).length;
        if (count < strengthConfig.required_uppercase_chars)
            errors.push({
                id: "validation.password.uppercase",
                values: { min: strengthConfig.required_uppercase_chars },
            });
    }

    if (strengthConfig.required_numbers) {
        const count = (password.match(/[0-9]/g) || []).length;
        if (count < strengthConfig.required_numbers)
            errors.push({ id: "validation.password.digits", values: { min: strengthConfig.required_numbers } });
    }

    if (strengthConfig.required_special_chars) {
        const regexp = new RegExp(`[${PASSWORD_SPECIAL_CHARACTERS}]`, "g");
        const count = (password.match(regexp) || []).length;
        if (count < strengthConfig.required_special_chars)
            errors.push({
                id: "validation.password.special",
                values: { min: strengthConfig.required_special_chars },
            });
    }

    return errors;
}
